import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { reorderItems } from '../tier0/kit-sortable.mjs';
import type { FormController } from '../tier0/form-controller.mjs';

export function useSortableGesture(
  ids: string[],
  controller: FormController,
  newOperationId: () => string,
  announce: (id: string, position: number, count: number) => string,
  contextKey = '',
) {
  const [staged, setGesture] = useState<{
    id: string;
    order: string[];
    identity: string;
    controller: FormController;
  } | null>(null);
  const [notice, setMessage] = useState<{
    text: string;
    identity: string;
    controller: FormController;
  } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const locked = ['pending', 'unknown', 'blocked'].includes(snapshot.status);
  const identity = JSON.stringify([ids, contextKey]);
  const gesture =
    !locked && staged?.identity === identity && staged.controller === controller ? staged : null;
  const message =
    !locked && notice?.identity === identity && notice.controller === controller ? notice.text : '';
  const say = (id: string, position: number) =>
    setMessage({ text: announce(id, position, ids.length), identity, controller });
  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => {
    setGesture(null);
    setMessage(null);
    clearTimer();
  }, [identity, controller, locked]);
  useEffect(() => clearTimer, []);
  const begin = (id: string) => {
    if (locked || !ids.includes(id)) return;
    setGesture({ id, order: [...ids], identity, controller });
    say(id, ids.indexOf(id) + 1);
  };
  const preview = (beforeId: string | null) => {
    if (!gesture || locked) return;
    const order = reorderItems(
      gesture.order.map((id) => ({ id })),
      gesture.id,
      beforeId,
    ).map((item) => item.id);
    setGesture({ ...gesture, order });
    say(gesture.id, order.indexOf(gesture.id) + 1);
  };
  const commitIntent = async (id: string, beforeId: string | null, columnId?: string) => {
    if (locked) return;
    setGesture(null);
    clearTimer();
    controller.setField('move', { itemId: id, beforeId, ...(columnId ? { columnId } : {}) });
    await controller.submit(newOperationId());
  };
  const drop = async (columnId?: string) => {
    if (!gesture || locked) return;
    const position = gesture.order.indexOf(gesture.id);
    if (!columnId && JSON.stringify(gesture.order) === JSON.stringify(ids)) {
      setGesture(null);
      return;
    }
    await commitIntent(gesture.id, gesture.order[position + 1] ?? null, columnId);
  };
  const step = (id: string, direction: -1 | 1) => {
    if (!gesture || gesture.id !== id) return;
    const position = gesture.order.indexOf(id);
    if (
      (direction === -1 && position === 0) ||
      (direction === 1 && position === gesture.order.length - 1)
    )
      return;
    preview(direction === -1 ? gesture.order[position - 1] : (gesture.order[position + 2] ?? null));
  };
  return {
    gesture,
    message,
    snapshot,
    locked,
    begin,
    preview,
    drop,
    step,
    commitIntent,
    cancel: () => {
      if (gesture) say(gesture.id, ids.indexOf(gesture.id) + 1);
      setGesture(null);
      clearTimer();
    },
    longPress: (id: string) => {
      clearTimer();
      timer.current = setTimeout(() => begin(id), 300);
    },
    clearTimer,
  };
}
